import axios from 'axios';

function isAxiosCancelledError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const candidate = error as { code?: unknown; name?: unknown };
  if (candidate.code === 'ERR_CANCELED' || candidate.name === 'CanceledError') {
    return true;
  }

  if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(error)) {
    return error.code === 'ERR_CANCELED' || error.name === 'CanceledError';
  }

  return false;
}

export class ShapefileExportCancelledError extends Error {
  constructor() {
    super('Export cancelled');
    this.name = 'ShapefileExportCancelledError';
  }
}

export function throwIfExportCancelled(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new ShapefileExportCancelledError();
  }
}

function isShapefileExportCancelled(error: unknown): boolean {
  return error instanceof ShapefileExportCancelledError;
}

export function isExportAbortError(
  error: unknown,
  signal?: AbortSignal,
): boolean {
  if (signal?.aborted) {
    return true;
  }
  if (isShapefileExportCancelled(error)) {
    return true;
  }
  if (isAxiosCancelledError(error)) {
    return true;
  }
  return error instanceof DOMException && error.name === 'AbortError';
}

export async function cancellableMapPool<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
  options?: {
    readonly signal?: AbortSignal;
    readonly onProgress?: (completed: number, total: number) => void;
  },
): Promise<R[]> {
  const total = items.length;
  if (total === 0) {
    return [];
  }

  const signal = options?.signal;
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  let completed = 0;

  async function worker(): Promise<void> {
    while (true) {
      throwIfExportCancelled(signal);
      const index = nextIndex++;
      if (index >= total) {
        return;
      }
      results[index] = await fn(items[index], index);
      completed += 1;
      options?.onProgress?.(completed, total);
    }
  }

  const workers = Math.min(Math.max(1, limit), total);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}
